// Blog posts — recurring content engine for organic SEO.
// Each post is a self-contained markdown-style structure rendered by BlogPostPage.
export const blogPosts = [
  {
    slug: 'what-to-expect-at-aloha-tequila-arts-festival',
    title: "What to Expect at the Aloha Tequila & Arts Festival 2026",
    excerpt:
      "Your first time at a tequila festival? Here's exactly what happens, what to bring, what to drink, and how to make the most of June 20, 2026 at International Market Place.",
    metaTitle: "What to Expect at the Aloha Tequila & Arts Festival 2026 | Festival Guide",
    metaDescription:
      "First-time tequila festival guide for Aloha Tequila & Arts Festival 2026. What's included, what to bring, how to taste, and how to make the most of your night in Waikiki.",
    keywords:
      'aloha tequila festival guide, what to expect tequila festival, first time tequila festival, Waikiki festival tips',
    publishedAt: '2026-02-20',
    readTime: '5 min read',
    tag: 'Festival Guide',
    coverImage:
      'https://images.unsplash.com/photo-1717116068433-c0896dbd563c?w=1600&q=80',
    blocks: [
      {
        type: 'p',
        text: "If this is your first tequila festival — or your first time at the Aloha Tequila & Arts Festival specifically — here's a complete walkthrough of what your night will look like, what's included with your ticket, and a few pro tips for getting the most out of June 20, 2026 in Waikiki."
      },
      { type: 'h2', text: 'The basics' },
      {
        type: 'p',
        text: "The festival runs from 5:00 PM to 9:00 PM at International Market Place, 2330 Kalakaua Ave in the heart of Waikiki. It's a 21+ event — bring a valid government-issued ID. Tickets are sold via TicketSpice and include access to all tasting sections, live entertainment, and the food and art zones."
      },
      { type: 'h2', text: 'What you get at the door' },
      {
        type: 'list',
        items: [
          'A wristband confirming you\'re 21+ (so you can drink anywhere on-site)',
          'A festival tasting glass for tequila, mezcal and agave spirits pours',
          'Full access to all tasting sections, food vendors, the flea market, and the live entertainment areas'
        ]
      },
      { type: 'h2', text: 'How to taste like a pro (without getting wrecked)' },
      {
        type: 'p',
        text: "Pours are 1/2 oz, but they add up fast. Here\'s the rhythm experienced tasters use: start with one or two Blancos to calibrate your palate, move into Reposados, then sip a Mezcal or two for contrast. Drink one bottle of water per 3 pours. Eat tacos between sections — the food is part of the ticket. Skip the cocktails until the back half of the night so you can taste the spirits clean first."
      },
      { type: 'h2', text: 'The sections you don\'t want to miss' },
      {
        type: 'list',
        items: [
          'Additive-Free Alliance — pure agave, no glycerin or caramel coloring (cleaner finish, less hangover)',
          'Big Name Brands — the household tequilas tasted side-by-side with their premium expressions',
          'Mezcal/Sotol/Bacanora — smokier, wilder, more obscure. The category every serious agave drinker explores',
          'VIP Section — limited-pour rare bottles you almost never see at retail'
        ]
      },
      { type: 'h2', text: 'What to bring (and what not to)' },
      {
        type: 'list',
        items: [
          'Bring: ID, comfortable shoes, a phone charger or battery, light layer for after sunset',
          'Skip: backpacks (they slow you down at security), heels (Kalakaua is uneven in spots), expectations of a quiet night'
        ]
      },
      { type: 'h2', text: 'Getting there & getting home' },
      {
        type: 'p',
        text: "International Market Place is dead-center in Waikiki — most hotels along Kalakaua and Kuhio are a 5–10 minute walk. If you're not staying in walking distance, take a rideshare. Don't drive. Trade-wind evenings + tequila tasting + Kalakaua traffic is not a combination that ends well."
      },
      { type: 'h2', text: 'A reminder about why you\'re here' },
      {
        type: 'p',
        text: "Partial proceeds from the festival benefit the Aloha Cancer Project — a Honolulu 501(c)(3) supporting cancer patients across Hawaii. So while you\'re tasting world-class agave spirits and dancing to a DJ set under palm trees, you\'re also funding patient services and community outreach. Aloha indeed."
      }
    ]
  },
  {
    slug: 'tequila-101-beginners-guide',
    title: 'Tequila 101: A Beginner\'s Guide for Festival-Goers',
    excerpt:
      "Blanco vs. Reposado vs. Añejo, mezcal vs. sotol, why additive-free matters, and how to taste tequila like you mean it. Read this before June 20, 2026.",
    metaTitle: 'Tequila 101: Beginner\'s Guide to Tequila & Mezcal | Aloha Tequila Fest',
    metaDescription:
      "A clear, no-snobbery beginner's guide to tequila and agave spirits — Blanco, Reposado, Añejo, Mezcal, Sotol, Bacanora — and how to taste them at the Aloha Tequila & Arts Festival 2026.",
    keywords:
      'tequila 101, tequila beginners guide, blanco reposado anejo, mezcal vs tequila, additive free tequila, agave spirits guide',
    publishedAt: '2026-02-20',
    readTime: '7 min read',
    tag: 'Tequila Education',
    coverImage:
      'https://images.unsplash.com/photo-1693195083953-c9cdc5fc67ed?w=1600&q=80',
    blocks: [
      {
        type: 'p',
        text: "Walking into a tequila festival without knowing the basics is like walking into a wine festival having only ever drunk White Claw. You'll have a fine time — but you'll have a much better time if you know what you're tasting. This is the no-snobbery, festival-friendly version of Tequila 101."
      },
      { type: 'h2', text: 'What tequila actually is' },
      {
        type: 'p',
        text: 'Tequila is a distilled spirit made from the fermented juice of the blue Weber agave plant. By Mexican law, it can only be produced in five specific states (mostly Jalisco). At least 51% of the sugars must come from blue agave — but the bottles you actually want are 100% blue agave, which will say so on the label.'
      },
      { type: 'h2', text: 'The three main styles you\'ll see at the festival' },
      {
        type: 'list',
        items: [
          'Blanco (a.k.a. Plata or Silver) — unaged or rested briefly. Tastes like the agave plant: vegetal, peppery, citrus. The truest expression of the distillery\'s style.',
          'Reposado — aged 2 to 12 months in oak. The agave is still front and center, but you get vanilla, caramel, light spice from the barrel.',
          'Añejo — aged 1 to 3 years. Smoother, woodier, more dessert-like. Sip neat. Skip in cocktails.'
        ]
      },
      { type: 'h2', text: '"Additive-free" — why it matters' },
      {
        type: 'p',
        text: "Most mass-market tequilas are legally allowed to add up to 1% additives — caramel coloring, glycerin, oak extract, sugar syrup. It\'s a way to fake age and smooth things out. Additive-free brands skip all of it. The result: cleaner agave flavor, no synthetic vanilla finish, and (most drinkers report) noticeably less hangover. The Additive-Free Alliance section at the festival is where you taste the difference."
      },
      { type: 'h2', text: 'Mezcal — tequila\'s smokier cousin' },
      {
        type: 'p',
        text: 'Mezcal can be made from 30+ agave varietals (most commonly Espadín). The agave is roasted in earthen pits before fermentation, which gives mezcal its distinctive smoky, savory, almost campfire character. If you like Islay scotch or smoky cocktails, mezcal will become your new favorite spirit.'
      },
      { type: 'h2', text: 'Sotol & Bacanora — the deep cuts' },
      {
        type: 'p',
        text: "Sotol comes from the Dasylirion plant (technically not agave, but close cousin) and is made in northern Mexico. Bacanora is made from a specific wild agave in Sonora. Both are wilder, more rustic, more terroir-driven than tequila. You\'ll find them in the Agave Spirits section — try them once and you\'ll spend the next year hunting bottles."
      },
      { type: 'h2', text: 'How to actually taste tequila at a festival' },
      {
        type: 'list',
        items: [
          '1. Look at it. Color hints at age and barrel use.',
          '2. Sniff at arm\'s length first, then move closer. Identify agave (vegetal), citrus, pepper, vanilla, smoke.',
          '3. Take a tiny first sip — let it coat your tongue.',
          '4. Take a second, slightly bigger sip. Where does the flavor finish? Does it linger or fade?',
          '5. Drink water. Eat something. Move to the next pour.'
        ]
      },
      { type: 'h2', text: 'A few brands to seek out at Aloha Tequila Fest' },
      {
        type: 'p',
        text: 'Confirmed at the festival: Kapena, Codigo, El Tesoro, Tequila Ocho, Fortaleza, G4, 3 Tres, Cuervo, La Familia, Dobel, Herradura, Corralejo, Cascahuín, Espolón and more to come. If this is your first time, taste a Blanco from each and compare — you\'ll learn more about distillery style in 30 minutes than you would in 30 cocktails.'
      },
      { type: 'h2', text: 'See you on June 20' },
      {
        type: 'p',
        text: "You\'re ready. International Market Place, Waikiki, 5–9 PM, June 20, 2026. Tickets via TicketSpice. Bring an ID and an open palate."
      }
    ]
  }
];
